import React, { ReactElement, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import { websiteContainerCronjobSteps } from "constants/server";

import IconSelector from "components/shared/images/IconSelector";
import AppModal from "components/shared/modal/AppModal";

import { getCronjobTypes, postCreateCronjob } from "redux/handlers/webAppHandler";

import { useAppDispatch } from "hooks/reduxHook";

import { ICronjobTypeOptions } from "types/api/dockerApiInterface";

import CronRecap from "./cronjobStepComponents/CronRecap";

const CreateCronjob = ({ id, isWp }: { id: number; isWp: boolean }): ReactElement => {
 const intl = useIntl();
 const dispatch = useAppDispatch();
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("lg"));

 const [open, setOpen] = useState<boolean>(false);
 const [loading, setLoading] = useState<boolean>(false);
 const [file, setFile] = useState<string>("");
 const [timingWeek, setTimingWeek] = useState<string>("*");
 const [timingMin, setTimingMin] = useState<string>("*/5");
 const [timingHour, setTimingHour] = useState<string>("*");
 const [timingDay, setTimingDay] = useState<string>("*");
 const [timingMonth, setTimingMonth] = useState<string>("*");
 const [selectedType, setSelectedType] = useState<string>("5min");
 const [goToStepper, setGoToStepper] = useState<boolean>(false);
 const [activeStep, setActiveStep] = useState<number>(0);
 const [cronTypes, setCronTypes] = useState<Array<ICronjobTypeOptions>>([]);
 const [selectedCronType, setSelectedCronType] = useState<string>("");
 const [selectedOptionalValues, setSelectedOptionalValue] = useState<string>("");

 const handleOpen = async () => {
  setGoToStepper(false);
  handleReset();
  setCronTypes(await dispatch(getCronjobTypes()));
  setOpen(true);
 };
 const handleClose = () => setOpen(false);

 const handleConfirm = async () => {
  setLoading(true);
  await dispatch(
   postCreateCronjob(
    id,
    file,
    `"${timingMin} ${timingHour} ${timingDay} ${timingMonth} ${timingWeek}"`,
    isWp,
    selectedCronType,
    selectedOptionalValues
   )
  );
  setLoading(false);
  handleClose();
 };

 const handleNext = () => {
  setActiveStep((prevActiveStep) => prevActiveStep + 1);
 };

 const handleBack = () => {
  setActiveStep((prevActiveStep) => prevActiveStep - 1);
 };

 const handleReset = () => {
  setFile("");
  setTimingWeek("*");
  setTimingMin("*");
  setTimingHour("*");
  setTimingDay("*");
  setTimingMonth("*");
  setSelectedType("5min");
  setActiveStep(0);
 };

 const changeSelection = (selection: string) => {
  setSelectedType(selection);
  switch (selection) {
   case "5min": {
    setTimingDay("*");
    setTimingHour("*");
    setTimingMin("*/5");
    setTimingWeek("*");
    setTimingMonth("*");
    break;
   }
   case "hourly": {
    setTimingDay("*");
    setTimingHour("*");
    setTimingMin("0");
    setTimingWeek("*");
    setTimingMonth("*");
    break;
   }
   case "daily": {
    setTimingDay("*");
    setTimingHour("0");
    setTimingMin("0");
    setTimingWeek("*");
    setTimingMonth("*");
    break;
   }
   case "weekly": {
    setTimingDay("*");
    setTimingHour("0");
    setTimingMin("0");
    setTimingWeek("0");
    setTimingMonth("*");
    break;
   }
   case "monthly": {
    setTimingDay("1");
    setTimingHour("0");
    setTimingMin("0");
    setTimingWeek("*");
    setTimingMonth("*");
    break;
   }
   case "custom": {
    setTimingDay("*");
    setTimingHour("*");
    setTimingMin("*");
    setTimingWeek("*");
    setTimingMonth("*");
    break;
   }
  }
 };

 const RenderCronInputs = (): ReactElement => {
  switch (selectedType) {
   case "5min":
    return (
     <Stack direction="row" alignItems="center">
      <Typography variant="subtitle2" fontStyle="italic">
       <FormattedMessage id="sites.cronjob.5MinutesExplain" />
      </Typography>
     </Stack>
    );
   case "hourly":
    return (
     <TextField
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
       e.stopPropagation();
      }}
      size="small"
      value={timingMin}
      sx={{ width: desktopViewPort ? "auto" : "100%", maxWidth: desktopViewPort ? 120 : "stretch" }}
      label={<FormattedMessage id="sites.cronjob.run_min" />}
      onChange={({ currentTarget }) => setTimingMin(currentTarget.value)}
      InputLabelProps={{ shrink: true }}
      autoComplete="off"
     />
    );
   case "daily":
    return (
     <Stack
      direction={desktopViewPort ? "row" : "column"}
      alignItems="center"
      spacing={desktopViewPort ? 0 : 2}>
      <TextField
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       size="small"
       value={timingMin}
       sx={{
        width: desktopViewPort ? "auto" : "100%",
        maxWidth: desktopViewPort ? 120 : "stretch"
       }}
       label={<FormattedMessage id="sites.cronjob.run_min" />}
       onChange={({ currentTarget }) => setTimingMin(currentTarget.value)}
       InputLabelProps={{ shrink: true }}
       autoComplete="off"
      />
      {desktopViewPort && <Typography>{"-"}</Typography>}
      <TextField
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       size="small"
       value={timingHour}
       sx={{
        width: desktopViewPort ? "auto" : "100%",
        maxWidth: desktopViewPort ? 120 : "stretch"
       }}
       label={<FormattedMessage id="sites.cronjob.run_hour" />}
       onChange={({ currentTarget }) => setTimingHour(currentTarget.value)}
       InputLabelProps={{ shrink: true }}
       autoComplete="off"
      />
     </Stack>
    );
   case "weekly":
    return (
     <Stack
      direction={desktopViewPort ? "row" : "column"}
      alignItems="center"
      spacing={desktopViewPort ? 0 : 2}>
      <TextField
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       size="small"
       value={timingMin}
       sx={{
        width: desktopViewPort ? "auto" : "100%",
        maxWidth: desktopViewPort ? 120 : "stretch"
       }}
       label={<FormattedMessage id="sites.cronjob.run_min" />}
       onChange={({ currentTarget }) => setTimingMin(currentTarget.value)}
       InputLabelProps={{ shrink: true }}
       autoComplete="off"
      />
      {desktopViewPort && <Typography>{"-"}</Typography>}
      <TextField
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       size="small"
       value={timingHour}
       sx={{
        width: desktopViewPort ? "auto" : "100%",
        maxWidth: desktopViewPort ? 120 : "stretch"
       }}
       label={<FormattedMessage id="sites.cronjob.run_hour" />}
       onChange={({ currentTarget }) => setTimingHour(currentTarget.value)}
       InputLabelProps={{ shrink: true }}
       autoComplete="off"
      />
      {desktopViewPort && <Typography>{"-"}</Typography>}
      <TextField
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       size="small"
       value={timingWeek}
       sx={{
        width: desktopViewPort ? "auto" : "100%",
        maxWidth: desktopViewPort ? 120 : "stretch"
       }}
       label={<FormattedMessage id="sites.cronjob.run_wday" />}
       onChange={({ currentTarget }) => setTimingWeek(currentTarget.value)}
       InputLabelProps={{ shrink: true }}
       autoComplete="off"
      />
     </Stack>
    );
   case "monthly":
    return (
     <Stack
      direction={desktopViewPort ? "row" : "column"}
      alignItems="center"
      spacing={desktopViewPort ? 0 : 2}>
      <TextField
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       size="small"
       value={timingMin}
       sx={{
        width: desktopViewPort ? "auto" : "100%",
        maxWidth: desktopViewPort ? 120 : "stretch"
       }}
       label={<FormattedMessage id="sites.cronjob.run_min" />}
       onChange={({ currentTarget }) => setTimingMin(currentTarget.value)}
       InputLabelProps={{ shrink: true }}
       autoComplete="off"
      />
      {desktopViewPort && <Typography>{"-"}</Typography>}
      <TextField
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       size="small"
       value={timingHour}
       sx={{
        width: desktopViewPort ? "auto" : "100%",
        maxWidth: desktopViewPort ? 120 : "stretch"
       }}
       label={<FormattedMessage id="sites.cronjob.run_hour" />}
       onChange={({ currentTarget }) => setTimingHour(currentTarget.value)}
       InputLabelProps={{ shrink: true }}
       autoComplete="off"
      />
      {desktopViewPort && <Typography>{"-"}</Typography>}
      <TextField
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       size="small"
       value={timingDay}
       sx={{
        width: desktopViewPort ? "auto" : "100%",
        maxWidth: desktopViewPort ? 120 : "stretch"
       }}
       label={<FormattedMessage id="sites.cronjob.run_mday" />}
       onChange={({ currentTarget }) => setTimingDay(currentTarget.value)}
       InputLabelProps={{ shrink: true }}
       autoComplete="off"
      />
     </Stack>
    );
   case "custom":
    return (
     <Stack
      direction={desktopViewPort ? "row" : "column"}
      alignItems="center"
      spacing={desktopViewPort ? 0 : 2}>
      <TextField
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       size="small"
       value={timingMin}
       sx={{
        width: desktopViewPort ? "auto" : "100%",
        maxWidth: desktopViewPort ? 120 : "stretch"
       }}
       label={<FormattedMessage id="sites.cronjob.run_min" />}
       onChange={({ currentTarget }) => setTimingMin(currentTarget.value)}
       InputLabelProps={{ shrink: true }}
       autoComplete="off"
      />
      {desktopViewPort && <Typography>{"-"}</Typography>}
      <TextField
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       size="small"
       value={timingHour}
       sx={{
        width: desktopViewPort ? "auto" : "100%",
        maxWidth: desktopViewPort ? 120 : "stretch"
       }}
       label={<FormattedMessage id="sites.cronjob.run_hour" />}
       onChange={({ currentTarget }) => setTimingHour(currentTarget.value)}
       InputLabelProps={{ shrink: true }}
       autoComplete="off"
      />
      {desktopViewPort && <Typography>{"-"}</Typography>}
      <TextField
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       size="small"
       value={timingDay}
       sx={{
        width: desktopViewPort ? "auto" : "100%",
        maxWidth: desktopViewPort ? 120 : "stretch"
       }}
       label={<FormattedMessage id="sites.cronjob.run_mday" />}
       onChange={({ currentTarget }) => setTimingDay(currentTarget.value)}
       InputLabelProps={{ shrink: true }}
       autoComplete="off"
      />
      {desktopViewPort && <Typography>{"-"}</Typography>}
      <TextField
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       size="small"
       value={timingMonth}
       sx={{
        width: desktopViewPort ? "auto" : "100%",
        maxWidth: desktopViewPort ? 120 : "stretch"
       }}
       label={<FormattedMessage id="sites.cronjob.run_month" />}
       onChange={({ currentTarget }) => setTimingMonth(currentTarget.value)}
       InputLabelProps={{ shrink: true }}
       autoComplete="off"
      />
      {desktopViewPort && <Typography>{"-"}</Typography>}
      <TextField
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       size="small"
       value={timingWeek}
       sx={{
        width: desktopViewPort ? "auto" : "100%",
        maxWidth: desktopViewPort ? 120 : "stretch"
       }}
       label={<FormattedMessage id="sites.cronjob.run_wday" />}
       onChange={({ currentTarget }) => setTimingWeek(currentTarget.value)}
       InputLabelProps={{ shrink: true }}
       autoComplete="off"
      />
     </Stack>
    );
   default:
    return <></>;
  }
 };

 const RenderStepContent = (): ReactElement => {
  switch (activeStep) {
   case 0:
    return (
     <Stack spacing={2}>
      <FormControl>
       <InputLabel>
        <FormattedMessage id="docker.website.cronjobType" />
       </InputLabel>
       <Select
        value={selectedCronType}
        label={<FormattedMessage id="docker.website.cronjobType" />}
        onChange={(e) => setSelectedCronType(e.target.value)}>
        <MenuItem value="php">PHP</MenuItem>
        <MenuItem value="sh">SH</MenuItem>
        <MenuItem value="wget">WGET</MenuItem>
        <MenuItem value="command">COMMAND</MenuItem>
       </Select>
      </FormControl>
      <TextField
       autoComplete="off"
       value={file}
       autoFocus
       label={intl.formatMessage({ id: "docker.website.cronjobFile" })}
       InputLabelProps={{ shrink: true }}
       onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        e.stopPropagation();
       }}
       onChange={({ currentTarget }) => setFile(currentTarget.value)}
       helperText={
        isWp
         ? "es: Cron Php: /var/www/html/nomefile.php, Cron Sh: sh /var/www/html/nomefile.sh. Cron WP CLI: /usr/local/bin/wp lsa:nomecomando"
         : "es: Cron Php: /var/www/html/nomefile.php, Cron Sh: sh /var/www/html/nomefile.sh"
       }
      />
      {(!isWp || selectedCronType !== "command") && (
       <FormControl>
        <InputLabel>
         <FormattedMessage id="docker.website.cronjobOptional" />
        </InputLabel>
        <Select
         value={selectedOptionalValues}
         disabled={selectedCronType === ""}
         label={<FormattedMessage id="docker.website.cronjobOptional" />}
         onChange={(e) => setSelectedOptionalValue(e.target.value)}>
         {cronTypes
          .find((element) => element.type === selectedCronType)
          ?.options.map((element, index) => {
           return (
            <MenuItem key={`optional-value-${index}`} value={element}>
             {element}
            </MenuItem>
           );
          })}
        </Select>
       </FormControl>
      )}
     </Stack>
    );
   case 1:
    return (
     <Stack spacing={3}>
      <Alert severity="info">
       <Typography variant="subtitle2">
        <FormattedMessage id="sites.cronjob.timingExplain" />
       </Typography>
      </Alert>
      <Stack direction={desktopViewPort ? "row" : "column"} spacing={2}>
       <FormControl>
        <InputLabel>
         <FormattedMessage id="sites.cronjob.timing" />
        </InputLabel>
        <Select
         value={selectedType}
         size="small"
         sx={{ width: desktopViewPort ? "fit-content" : "stretch" }}
         label={<FormattedMessage id="sites.cronjob.timing" />}
         onChange={(e) => changeSelection(e.target.value)}>
         <MenuItem value={"5min"}>Ogni 5 minuti</MenuItem>
         <MenuItem value={"hourly"}>Orario</MenuItem>
         <MenuItem value={"daily"}>Giornaliero</MenuItem>
         <MenuItem value={"weekly"}>Settimanale</MenuItem>
         <MenuItem value={"monthly"}>Mensile</MenuItem>
         <MenuItem value={"custom"}>Custom</MenuItem>
        </Select>
       </FormControl>
       <RenderCronInputs />
      </Stack>
     </Stack>
    );
   case 2:
    return (
     <CronRecap
      file={file}
      type={selectedCronType}
      optional={selectedOptionalValues}
      timing={{
       week: timingWeek,
       minutes: timingMin,
       hours: timingHour,
       days: timingDay,
       month: timingMonth
      }}
     />
    );
   default:
    return <></>;
  }
 };

 const renderThirdText = (): string => {
  if (!goToStepper) {
   return "docker.website.startNow";
  } else if (activeStep === 2) {
   return "app.confirm";
  } else {
   return "app.next";
  }
 };

 const handleConfirmThird = () => {
  if (!goToStepper) {
   setGoToStepper(true);
  } else if (activeStep === 2) {
   handleConfirm();
  } else {
   handleNext();
  }
 };

 const checkDisableThird = (): boolean => {
  return (
   goToStepper &&
   (loading ||
    (activeStep === 0 && file === "" && selectedCronType === "") ||
    (activeStep === 1 &&
     (timingWeek === "" ||
      timingMin === "" ||
      timingHour === "" ||
      timingDay === "" ||
      timingMonth === "")))
  );
 };

 return (
  <>
   <Button onClick={handleOpen}>
    <FormattedMessage id="docker.website.createCronjob" />
   </Button>
   <AppModal
    open={open}
    close={handleClose}
    title={intl.formatMessage({ id: "docker.website.createCronjob" })}
    handleClose={() => (goToStepper ? handleBack() : handleClose())}
    handleConfirm={handleReset}
    thirdOption={true}
    thirdText={renderThirdText()}
    disabled={loading}
    handleConfirmThird={handleConfirmThird}
    confirmText={"app.reset"}
    disableCancel={activeStep === 0 && goToStepper}
    showConfirmButton={goToStepper}
    disabledThird={loading || checkDisableThird()}>
    {!goToStepper ? (
     <Stack>
      <Stack direction="row">
       <Stack justifyContent="center" spacing={2}>
        <Typography>
         <FormattedMessage id="docker.website.secondExplainCronjob" />
        </Typography>
        <Typography>
         <FormattedMessage id="docker.website.thirdExplainCronjob" />
        </Typography>
       </Stack>
       <Stack alignItems="center" justifyContent="center">
        <IconSelector
         icon="CronjobIcon"
         props={{ sx: { fontSize: "200px", color: "lightgray" } }}
        />
       </Stack>
      </Stack>
     </Stack>
    ) : (
     <Stack>
      <Stepper activeStep={activeStep} alternativeLabel>
       {websiteContainerCronjobSteps.map((label) => (
        <Step key={label.label}>
         <StepLabel optional={label.description ? <FormattedMessage id={label.description} /> : ""}>
          {<FormattedMessage id={label.label} />}
         </StepLabel>
        </Step>
       ))}
      </Stepper>
      <RenderStepContent />
     </Stack>
    )}
   </AppModal>
  </>
 );
};

export default CreateCronjob;
